// ---------------------------------------------------------------------------
// Auth ผ่าน PaymentBroker (เหมือน LevelUpCRM แต่ไม่มี multi-tenant shop)
//  - login/register/forgot ยิง PaymentBroker จริง · token เก็บ btoa("Bearer "+token) ที่ scp-acc-tk
//  - สิทธิ์ super_user ยึดจาก GET profile ของ Backend เท่านั้น (ค่าใน localStorage แก้เองได้ ห้ามเชื่อ)
//  - IS_MOCK: ใช้ผู้ใช้จำลองใน localStorage ไม่ยิงเน็ต
// ---------------------------------------------------------------------------
import { ENDPOINTS, CAPTCHA, IS_MOCK, USER_TYPE, AUTH_EVENT, TERMS_VERSION } from "./config";
import { http, ApiError, setToken, setStoredProfile, getStoredProfile, clearSession } from "./apiClient";
import { mockUsers } from "./mock";
import api from "./api";

const emit = () => window.dispatchEvent(new Event(AUTH_EVENT));
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const toProfile = (u) => ({
  email: u.email,
  displayName: u.displayName,
  username: u.username,
  user_type: u.user_type,
  tokensLeft: u.tokensLeft,
  tier: u.tier,
  termsAcceptedAt: u.termsAcceptedAt || null,
  termsVersion: u.termsVersion || "",
});

export const authService = {
  login: async ({ email, password, remember = false, captchaToken }) => {
    if (!validEmail(email) || !password) throw new ApiError("กรุณากรอกอีเมลและรหัสผ่านให้ถูกต้อง");

    if (IS_MOCK) {
      let user = mockUsers.get(email);
      if (!user) throw new ApiError("ไม่พบบัญชีนี้ (โหมดทดลอง: สมัครสมาชิกก่อน)");
      if (user.password !== btoa(password)) throw new ApiError("อีเมลหรือรหัสผ่านไม่ถูกต้อง");
      setToken(`mock-${user.id}`);
      setStoredProfile(toProfile(user));
      emit();
      return toProfile(user);
    }

    const result = await http.post(ENDPOINTS.login, {
      email: email.trim(),
      password: btoa(password),
      remember,
      token: captchaToken || CAPTCHA.masterToken,
    });
    if (!result.status) throw new ApiError(result.message, result);
    setToken(result.data.token);

    const profile = await authService.refreshProfile({ base: { ...result.data, token: undefined, password: "", email: email.trim() } });
    return profile;
  },

  /** อ่านโปรไฟล์ล่าสุดจาก Backend (+ข้อมูลแอปจาก GS) แล้วเก็บลง localStorage */
  refreshProfile: async ({ base } = {}) => {
    const stored = base || getStoredProfile() || {};
    if (IS_MOCK) {
      const u = mockUsers.get(stored.email);
      if (!u) return null;
      const p = toProfile(u);
      setStoredProfile(p);
      emit();
      return p;
    }
    const result = await http.get(ENDPOINTS.profile);
    if (!result.status) {
      if (result.expired) return null;
      return getStoredProfile();
    }
    const raw = result.data || {};
    let app = {};
    try {
      app = (await api.user.me()) || {};
    } catch (e) {
      /* GS ล่ม/ยังไม่มีผู้ใช้ — ใช้ข้อมูล PaymentBroker ไปก่อน */
    }
    const profile = {
      ...stored,
      ...raw,
      ...app,
      email: stored.email || raw.customer?.email || raw.email || "",
      displayName: app.displayName || raw.customer?.name_th || raw.full_name || stored.displayName || "",
      // สิทธิ์ super_user ยึดตาม Backend เท่านั้น
      user_type: raw.user_type === USER_TYPE.superUser ? USER_TYPE.superUser : USER_TYPE.user,
      customerUid: raw.customer?.uid || "",
      password: "",
    };
    setStoredProfile(profile);
    emit();
    return profile;
  },

  register: async ({ email, password, displayName, phone }) => {
    if (!validEmail(email)) throw new ApiError("รูปแบบอีเมลไม่ถูกต้อง");
    if (!password || password.length < 8) throw new ApiError("รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร");
    // PaymentBroker ต้องมีเบอร์ 10 หลัก (เหมือน CRM) — ไม่มีเบอร์ สมัครผ่านแต่ล็อกอินไม่ได้ (Invalid credentials or inactive account)
    phone = String(phone || "").replace(/\D/g, "");
    if (phone.length !== 10) throw new ApiError("กรุณากรอกเบอร์โทรศัพท์ 10 หลัก");

    if (IS_MOCK) {
      if (mockUsers.get(email)) throw new ApiError("อีเมลนี้สมัครไว้แล้ว กรุณาเข้าสู่ระบบ");
      mockUsers.create({ email, password, displayName, phone });
      return authService.login({ email, password });
    }

    const name = displayName || email.split("@")[0];
    const result = await http.post(ENDPOINTS.register, {
      name_th: name,
      name_en: name,
      phone: phone || "",
      email: email.trim(),
      password,
      company_address: "",
    });
    if (!result.status) throw new ApiError(result.message, result);
    return authService.login({ email, password });
  },

  forgotPassword: async (email) => {
    if (!validEmail(email)) throw new ApiError("รูปแบบอีเมลไม่ถูกต้อง");
    if (IS_MOCK) return { sent: true };
    const result = await http.post(ENDPOINTS.resetPassword, { email: email.trim() });
    if (!result.status) throw new ApiError(result.message, result);
    return result.data;
  },

  acceptTerms: async () => {
    await api.user.acceptTerms(TERMS_VERSION);
    const profile = { ...(getStoredProfile() || {}), termsAcceptedAt: new Date().toISOString(), termsVersion: TERMS_VERSION };
    setStoredProfile(profile);
    emit();
    return profile;
  },

  /** ถาม Backend ว่า token นี้เป็น super_user จริงไหม (RequireSuperUser เรียก) */
  verifySuperUser: async () => {
    if (IS_MOCK) return mockUsers.get(getStoredProfile()?.email)?.user_type === USER_TYPE.superUser;
    const result = await http.get(ENDPOINTS.profile);
    return !!result.status && result.data?.user_type === USER_TYPE.superUser;
  },

  logout: async () => {
    if (!IS_MOCK) {
      try {
        await http.get(ENDPOINTS.logout);
      } catch (e) {
        /* ignore */
      }
    }
    clearSession();
  },

  currentProfile: () => getStoredProfile(),
  isLoggedIn: () => !!getStoredProfile(),
};

export const hasAcceptedTerms = (profile) => !!profile?.termsAcceptedAt && profile?.termsVersion === TERMS_VERSION;
